import React from 'react';
import { SafeAreaView, StyleSheet, View } from 'react-native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { useSelector } from 'react-redux';

import TopNavigation from '../components/TopNavigation/TopNavigation';
import CallBanner from 'src/components/CallBanner/CallBanner';
import CarrierWidget from 'src/features/carrier/components/CarrierWidget/CarrierWidget';
import ProducerNotesWidget from 'src/features/company/components/ProducerNotesWidget/ProducerNotesWidget';
import SideNavigation from 'src/components/SideNavigation/SideNavigation';
import SummaryPanel from 'src/components/SummaryPanel/SummaryPanel';
import ProducerDesktopShell from 'src/components/ProducerDesktopShell/ProducerDesktopShell';
import { useDesktopLayout } from 'src/hooks/useDesktopLayout';
// Screens
import VehicleStartScreen from '../features/vehicles/screens/VehicleStart/VehicleStart';
import VehicleEditScreen from '../features/vehicles/screens/editVehicle/editVehicle';
import VehicleBypassScreen from '../features/vehicles/screens/VehicleBypass/VehicleBypass';

import DriverStartScreen from '../features/drivers/screens/DriverStart/DriverStart';
import DriverEditScreen from '../features/drivers/screens/EditDriver/EditDriver';
import DriverBypassScreen from '../features/drivers/screens/DriverBypass/DriverBypass';

import CompanyStartScreen from 'src/features/company/CompanyStart';
import CompanyLandingScreen from 'src/features/company/CompanyLanding';
import CompanyDetailsScreen from 'src/features/company/CompanyDetails';
import CompanyAddressScreen from 'src/features/company/CompanyAddress';
import GaragingAddressScreen from 'src/features/company/screens/GaragingAddressScreen/GaragingAddressScreen';
// import WaitingListScreen from 'src/features/user/screens/WaitingList/WaitingListScreen';

import Quote from '../features/quotes/Quote';
import QuoteSuccess from 'src/features/quotes/QuoteSuccess';

import Insurance from 'src/features/application/Insurance';
import Policy from '../features/application/Policy';
import RiskHistory from 'src/features/application/RiskHistory';
import GrowthAndOwnership from 'src/features/application/GrowthAndOwnership';
import Loss from 'src/features/application/Loss';
import Confirmation from 'src/features/application/Confirmation';

import AddFreightScreen from 'src/features/freight/screens/AddFreight/AddFreight';
import { RootState } from 'src/store/store';
import { TABS_INDEX } from 'src/constants/tabs';
import LoadingBar from 'src/components/LoadingBar/LoadingBar';
import FollowupScreen from 'src/features/company/screens/FollowupScreen/FollowupScreen';
import { URLS } from 'src/constants/urls';
import { themes } from 'src/theme/theme';

const Stack = createNativeStackNavigator();

export default function MainNavigator() {
  const filledTabsIndex = useSelector((state: RootState) => state.UI.filledTabsIndex);
  const company = useSelector((state: RootState) => state.company.company);
  const producerPhoneNumber = useSelector((state: RootState) => state.users.producerPhoneNumber);
  const lastQuoteScreen = useSelector((state: RootState) => state.UI.lastQuoteScreen);
  const { isDesktop, isProducer, showSummaryPanel, sidebarWidth, summaryPanelWidth } =
    useDesktopLayout();

  const isCurrentTabDisabled = (currentIndex: number) => filledTabsIndex >= currentIndex;
  const hasCompany = company && typeof company.id === 'number' && company.id > 0;

  // Shared tabs array
  const tabs = [
    {
      label: '1. START               ',
      route: '/company-landing',
      disabled: !isCurrentTabDisabled(TABS_INDEX.COMPANY),
    },
    {
      label: '2. VEHICLES               ',
      route: '/vehicle',
      disabled: !isCurrentTabDisabled(TABS_INDEX.VEHICLE),
    },
    {
      label: '3. DRIVERS               ',
      route: '/driver-license',
      disabled: !isCurrentTabDisabled(TABS_INDEX.DRIVER),
    },
    {
      label: '4. FREIGHT               ',
      route: '/add-freight',
      disabled: !isCurrentTabDisabled(TABS_INDEX.FREIGHT),
    },
    {
      label: '5. FINAL DETAILS             ',
      route: '/insurance',
      disabled: !isCurrentTabDisabled(TABS_INDEX.FINAL_DETAILS),
    },
    {
      label: '6. QUOTES             ',
      route: lastQuoteScreen === 'PURCHASE' ? URLS.QUOTE.PURCHASE : URLS.QUOTE.SUCCESS,
      disabled: !isCurrentTabDisabled(TABS_INDEX.QUOTES),
    },
  ];

  const stackNavigator = (
    <Stack.Navigator
      screenOptions={{
        headerShown: false,
        headerTitle: 'Trucker Path Insurance',
        headerTitleAlign: 'center',
      }}>
      <Stack.Screen
        name="CompanyStartScreen"
        component={CompanyStartScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        name="CompanyLandingScreen"
        component={CompanyLandingScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        name="CompanyDetailsScreen"
        component={CompanyDetailsScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        name="CompanyAddressScreen"
        component={CompanyAddressScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        name="GaragingAddressScreen"
        component={GaragingAddressScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      {/* <Stack.Screen name="WaitingListScreen" component={WaitingListScreen} /> */}
      <Stack.Screen
        name="FollowupScreen"
        component={FollowupScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      {/*Vehicle*/}
      <Stack.Screen
        initialParams={{ id: undefined }}
        name="VehicleStartScreen"
        component={VehicleStartScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        initialParams={{ id: undefined }}
        name="VehicleEditScreen"
        component={VehicleEditScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        name="VehicleAddScreen"
        component={VehicleEditScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        name="VehicleBypassScreen"
        component={VehicleBypassScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      {/*Driver*/}
      <Stack.Screen
        initialParams={{ id: undefined }}
        name="DriverStartScreen"
        component={DriverStartScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        initialParams={{ id: undefined }}
        name="DriverEditScreen"
        component={DriverEditScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        name="DriverBypassScreen"
        component={DriverBypassScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        name="DriverAddScreen"
        component={DriverEditScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      {/* Quotes */}
      <Stack.Screen
        name="QuoteScreen"
        component={Quote}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        name="QuoteSuccessScreen"
        component={QuoteSuccess}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      {/**Freight */}
      <Stack.Screen
        name="AddFreightScreen"
        component={AddFreightScreen}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      {/* Application */}
      <Stack.Screen
        name="PolicyScreen"
        component={Policy}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        name="InsuranceScreen"
        component={Insurance}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        name="RiskHistoryScreen"
        component={RiskHistory}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        name="GrowthAndOwnershipScreen"
        component={GrowthAndOwnership}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        name="LossScreen"
        component={Loss}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
      <Stack.Screen
        name="ConfirmationScreen"
        component={Confirmation}
        options={{ headerTitle: 'Trucker Path Insurance' }}
      />
    </Stack.Navigator>
  );

  // Desktop Layout: Sidebar + Content + Summary Panel
  if (isDesktop) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.desktopLayout}>
          {/* Left Sidebar Navigation */}
          {hasCompany && (
            <SideNavigation
              tabs={tabs}
              width={sidebarWidth}
              showProducerExtras={isProducer && !showSummaryPanel}
            />
          )}
          {/* Center Content Area */}
          <View style={styles.desktopContent}>
            {hasCompany && <CallBanner phoneNumber={producerPhoneNumber} />}
            {isProducer && !showSummaryPanel && (
              <>
                <CarrierWidget />
                {hasCompany && !isDesktop && <ProducerNotesWidget />}
              </>
            )}
            <LoadingBar />
            <ProducerDesktopShell>{stackNavigator}</ProducerDesktopShell>
          </View>
          {/* Right Summary Panel (large desktop only) */}
          {showSummaryPanel && hasCompany && (
            <SummaryPanel width={summaryPanelWidth} showProducerTools={isProducer} />
          )}
        </View>
      </SafeAreaView>
    );
  }

  // Mobile / Regular User Layout (unchanged)
  return (
    <SafeAreaView style={styles.container}>
      {hasCompany && (
        <>
          <TopNavigation tabs={tabs} />
          <CallBanner phoneNumber={producerPhoneNumber} />
        </>
      )}
      {isProducer && (
        <>
          <CarrierWidget />
          {hasCompany && <ProducerNotesWidget />}
        </>
      )}
      <LoadingBar />
      <View style={styles.stackContainer}>{stackNavigator}</View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  stackContainer: {
    flex: 1,
  },
  desktopLayout: {
    flex: 1,
    flexDirection: 'row',
  },
  desktopContent: {
    flex: 1,
    backgroundColor: themes.light.gray[8],
  },
});
